import table from './clueCorners.json';
import { getIconSvg } from '../../services/iconService';

// Mirrors cw_clue_corners.dart in the app: which cells around a glyph box's
// bottom-right corner hold ink, in units of the box size, relative to that
// corner. clueCorners.json comes from tool/clue_corners/generate.py in the app
// repo, and this grid must match it.
export const CELL = 1 / 16;
const LEFT = -0.5;
const TOP = -0.5;
const COLS = 12;
const ROWS = 10;
const ALPHA_THRESHOLD = 40;
const CUSTOM_PX = 96;

const decoded = new Map();
const lookup = (kind, key) => {
  const id = `${kind}:${key}`;
  if (!decoded.has(id)) {
    const bits = table[kind][key];
    decoded.set(id, bits ? Uint8Array.from(atob(bits), c => c.charCodeAt(0)) : null);
  }
  return decoded.get(id);
};

// Keyed by code points in hex joined by '-', without U+FE0F, so a sequence the
// font draws as one glyph has its own map
const emojiKey = (emoji) => [...emoji].map(c => c.codePointAt(0)).filter(cp => cp !== 0xFE0F).map(cp => cp.toString(16)).join('-');

export const emojiCorner = (emoji) => lookup('emoji', emojiKey(emoji));

// How far the emoji's ink runs past the left of its box, in units of the box size
export const emojiOverhang = (emoji) => table.emojiOverhang[emojiKey(emoji)] ?? 0;

export const symbolCorner = (name) => lookup('symbols', name);

// Whether the rectangle overlaps ink. Past the grid's right and bottom there is
// none; past its left and top is unknown, so it counts as ink.
export const cornerHits = (bits, x0, y0, x1, y1) => {
  if (x0 < LEFT - 1e-9 || y0 < TOP - 1e-9) return true;
  const c0 = Math.floor((x0 - LEFT) / CELL + 1e-9);
  const c1 = Math.min(Math.ceil((x1 - LEFT) / CELL - 1e-9), COLS);
  const r0 = Math.floor((y0 - TOP) / CELL + 1e-9);
  const r1 = Math.min(Math.ceil((y1 - TOP) / CELL - 1e-9), ROWS);
  for (let r = r0; r < r1; r++) {
    for (let c = c0; c < c1; c++) {
      const i = r * COLS + c;
      if (bits[i >> 3] & (0x80 >> (i & 7))) return true;
    }
  }
  return false;
};

// Samples RGBA pixels whose bottom-right corner is the box's, at px pixels per box size
const fromRgba = (rgba, width, height, px) => {
  const grid = [];
  for (let r = 0; r < ROWS; r++) {
    grid.push([]);
    for (let c = 0; c < COLS; c++) {
      const x0 = Math.round(width + (LEFT + c * CELL) * px), x1 = Math.round(width + (LEFT + (c + 1) * CELL) * px);
      const y0 = Math.round(height + (TOP + r * CELL) * px), y1 = Math.round(height + (TOP + (r + 1) * CELL) * px);
      let ink = false;
      for (let y = Math.max(y0, 0); y < Math.min(y1, height) && !ink; y++) {
        for (let x = Math.max(x0, 0); x < Math.min(x1, width); x++) {
          if (rgba[(y * width + x) * 4 + 3] > ALPHA_THRESHOLD) { ink = true; break; }
        }
      }
      grid[r].push(ink);
    }
  }
  const bits = new Uint8Array((ROWS * COLS + 7) >> 3);
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      let grown = false;
      for (let dr = -1; dr <= 1 && !grown; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          const rr = r + dr, cc = c + dc;
          if (rr >= 0 && rr < ROWS && cc >= 0 && cc < COLS && grid[rr][cc]) { grown = true; break; }
        }
      }
      const i = r * COLS + c;
      if (grown) bits[i >> 3] |= 0x80 >> (i & 7);
    }
  }
  return bits;
};

// The SVG gets an explicit size first: with only a viewBox, browsers draw it into a default 300 × 150 box
const rasterize = (svg, width, height) => new Promise((resolve, reject) => {
  const doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
  doc.documentElement.setAttribute('width', width);
  doc.documentElement.setAttribute('height', height);
  doc.documentElement.setAttribute('preserveAspectRatio', 'none');
  const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(doc)], { type: 'image/svg+xml' }));
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    context.drawImage(img, 0, 0, width, height);
    URL.revokeObjectURL(url);
    resolve(context.getImageData(0, 0, width, height).data);
  };
  img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
  img.src = url;
});

const customCorners = new Map();

// width and height are the icon's box in units of the box size, which depend
// only on its aspect ratio, so the name alone identifies the result.
export const customCorner = (name, width, height) => {
  if (!customCorners.has(name)) {
    const w = Math.round(width * CUSTOM_PX), h = Math.round(height * CUSTOM_PX);
    customCorners.set(name, getIconSvg(name)
      .then(svg => (svg ? rasterize(svg, w, h) : null))
      .then(rgba => (rgba ? fromRgba(rgba, w, h, CUSTOM_PX) : null))
      .catch(() => null));
  }
  return customCorners.get(name);
};
